<template>
    <div class="filter-group">
        <div class="control-buttons" v-if="showRemoveButton">
            <div class="control-button">
                <md-button class="md-icon-button md-mini" @click="() => onRemoved(clone.id)">
                    <md-icon>delete_outline</md-icon>
                </md-button>
            </div>
        </div>

        <SingleFilter
            v-for="(filter, index) of clone.filters"
            v-model="clone.filters[index]"
            :enable-operator-changed="clone.filters.length > 1"
            :operator="clone.operator"
            :on-operator-changed="onOperatorChanged"
            :selected="filter.id === selectedFilterId"
            :on-selected="() => (selectedFilterId = filter.id)"
            :on-canceled="() => (selectedFilterId = null)"
            :on-removed="onFilterRemoved"
            :key="filter.id"
            :group-id="value.id"
            :on-group-select-hovered="(hovered) => (groupSelectHovered = hovered)"
            :group-select-hovered="groupSelectHovered"
        />
        <div class="filter-row" :class="{ empty: !clone.filters.length }">
            <a-popover
                v-model="attributePickerVisible"
                trigger="click"
                placement="left"
                :overlayClassName="'no-padding'"
            >
                <div slot="content">
                    <AutomationAttributePicker
                        :default-selected="null"
                        :pick="onAttributePicked"
                        :filter-mode="true"
                        :allowed-contexts="allowedContexts"
                        :prefix="attributeContextPrefix"
                    />
                </div>
                <div class="add-filter-button">
                    <span class="material-icons"> add </span>
                    {{ $t("chatbotv2.addNewFilter") }}
                </div>
            </a-popover>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import SingleFilter from "./SingleFilter";
import AutomationAttributePicker from "../../pickers/attribute/AutomationAttributePicker.vue";
import _ from "lodash";
import CONFIG from "../../../../../../../config/config";
import AutomationAttributesGenerator from "../../../../utils/AutomationAttributesGenerator";
import { mapState } from "vuex";

const log = log2("FilterGroup");
export default {
    name: "FilterGroup",
    components: {
        SingleFilter,
        AutomationAttributePicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                operator: "or",
                filters: [
                    {
                        id: generateRandomId(),
                        rule: "greater_or_equal",
                        attribute: "email",
                        value: 0,
                    },
                ],
            }),
        },
        onRemoved: {
            type: Function,
            required: true,
        },
        showRemoveButton: {
            type: Boolean,
            required: true,
        },
        allowedContexts: {
            type: Array,
            default: () => ["contact", "block", "event", "system", "custom", "webhook"],
        },
        attributeContextPrefix: {
            type: String,
        },
    },
    computed: {
        ...mapState({
            automation: (state) => state.automation.current,
        }),
    },
    data() {
        return {
            selectedFilterId: null,
            attributePickerVisible: false,
            clone: _.cloneDeep(this.value),
            groupSelectHovered: false,
        };
    },

    methods: {
        onOperatorChanged(operator) {
            log("onOperatorChanged", operator);
            this.clone.operator = operator;
        },
        onAttributePicked(attribute) {
            const id = generateRandomId();
            let value = 0;
            const attributeGenerator = AutomationAttributesGenerator.create();
            const attributes = attributeGenerator.list(this.automation, this.attributeContextPrefix);
            const foundAttr = attributes.find((x) => x.value === attribute);
            let rule = _.get(foundAttr, "defaultOperator", "equal");
            value = _.get(foundAttr, "defaultFilterValue", value);

            this.clone.filters.push({
                id,
                rule,
                attribute,
                value,
            });
            this.attributePickerVisible = false;
            this.selectedFilterId = id;
        },
        onFilterRemoved(id) {
            log("onFilterRemoved", id);
            this.clone.filters = this.clone.filters.filter((filter) => filter.id !== id);
            if (this.selectedFilterId === id) {
                this.selectedFilterId = null;
            }
        },
    },
    mounted() {
        log("mounted");
    },
    watch: {
        clone: {
            handler(newValue) {
                this.$emit("input", newValue);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.filter-group {
    grid-row-gap: 15px;
    background: #fff;
    margin: 0 10px;
    padding: 10px;
    border-radius: 4px;
    display: grid;
    position: relative;

    &:hover {
        .control-buttons {
            opacity: 1;
        }
    }

    .control-buttons {
        height: 100%;
        left: -44px;
        opacity: 0;
        position: absolute;
        top: 0;
        transition: opacity 0.3s;
        width: 44px;

        .md-button {
            background-color: #fff;
            align-items: center;
            border-radius: 100%;
            box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
            cursor: pointer;
            display: flex;
            justify-content: center;
            margin-bottom: 7px;
            transition: background-color 0.3s;
            height: 30px;
            width: 30px;
            min-height: 30px !important;
            min-width: 30px !important;

            .md-icon {
                color: #006cff;
            }
        }
    }

    .filter-row {
        grid-column-gap: 10px;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 10px;
        width: 100%;

        .add-filter-button {
            align-items: center;
            border: 1px solid #c6cdd4;
            border-radius: 4px;
            color: #006cff;
            cursor: pointer;
            display: flex;
            font-weight: 600;
            padding: 8px 15px;
            transition: border 0.3s, opacity 0.3s;

            &:hover {
                border-color: #006cff;
            }
        }

        &.empty {
            grid-template-columns: minmax(0, 1fr);
        }
    }
}
</style>
